import { useState } from 'react'
import { Modal } from '@datahub/design-system'
import type { SectionDef, FieldDef } from '@datahub/shared'
import { evaluateCondition, isFieldVisible, isFieldRequired } from '@datahub/shared'
import { FieldRenderer } from './FieldRenderer'
import type { DraftRow, ValidationIssue } from '../../lib/types'

interface RowDrawerProps {
  section: SectionDef
  open: boolean
  row: DraftRow | null
  profile: Record<string, unknown>
  controls: Record<string, unknown>
  issues: ValidationIssue[]
  onSave: (row: DraftRow) => void
  onClose: () => void
}

function blocksOf(section: SectionDef): { key: string; label: string; fields: FieldDef[]; visibleWhen?: unknown }[] {
  if (section.blocks?.length) return section.blocks as never
  return [{ key: 'fields', label: section.label, fields: section.fields }]
}

/** Right-aligned drawer for adding or editing one repeatable row (plan Section 12.6). */
export function RowDrawer({ section, open, row, profile, controls, issues, onSave, onClose }: RowDrawerProps) {
  const [draft, setDraft] = useState<DraftRow | null>(row)

  // Reset the local copy whenever a different row is opened.
  if (open && draft?.id !== row?.id) setDraft(row)
  if (!open || !draft) return null

  const ctx = { values: draft.values, controls, profile }
  const setValue = (key: string, value: unknown) => {
    setDraft({
      ...draft,
      values: { ...draft.values, [key]: value },
      confirmationState: draft.confirmationState === 'confirmed' ? 'edited' : draft.confirmationState,
    })
  }

  const errorFor = (field: FieldDef) =>
    issues.find((i) => i.rowId === draft.id && i.field === field.key && i.severity === 'error')?.message

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={row?.confirmationState === 'new' ? `Add to ${section.label}` : `Edit ${section.label}`}
      primaryLabel="Save"
      onPrimary={() => onSave(draft)}
      width={640}
    >
      <div className="flex flex-col" style={{ gap: 24 }}>
        {blocksOf(section).map((block) => {
          if (!evaluateCondition(block.visibleWhen as never, ctx)) return null
          const visible = block.fields.filter((f) => isFieldVisible(f, ctx))
          if (visible.length === 0) return null
          return (
            <div key={block.key} className="flex flex-col" style={{ gap: 20 }}>
              {blocksOf(section).length > 1 && (
                <div className="flex items-center" style={{ gap: 16 }}>
                  <span className="font-jakarta text-white flex-shrink-0" style={{ fontSize: 12 }}>{block.label}</span>
                  <span style={{ flex: 1, height: '0.5px', backgroundColor: '#4B5563' }} />
                </div>
              )}
              {visible.map((field) => (
                <div key={field.key}>
                  <FieldRenderer
                    field={{ ...field, label: isFieldRequired(field, ctx) ? `${field.label} *` : field.label }}
                    value={draft.values[field.key]}
                    onChange={(v) => setValue(field.key, v)}
                    {...(errorFor(field) ? { error: errorFor(field)! } : {})}
                    siblings={draft.values}
                  />
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </Modal>
  )
}
